import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Search } from 'lucide-react';
import { api } from '@/lib/api';
import BrandLogo from '@/components/BrandLogo';

export default function Brands() {
    const [brands, setBrands] = useState([]);
    const [q, setQ] = useState('');
    const [letter, setLetter] = useState('');

    useEffect(() => {
        api.get('/catalog/brands?limit=300').then(({ data }) => setBrands(data));
    }, []);

    const filtered = useMemo(() => {
        let r = brands;
        if (q) r = r.filter((b) => b.name.toLowerCase().includes(q.toLowerCase()));
        if (letter) r = r.filter((b) => b.name.toUpperCase().startsWith(letter));
        return r;
    }, [brands, q, letter]);

    const grouped = useMemo(() => {
        const map = {};
        filtered.forEach((b) => {
            const k = b.name[0]?.toUpperCase() || '#';
            (map[k] = map[k] || []).push(b);
        });
        return Object.entries(map).sort();
    }, [filtered]);

    const LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

    return (
        <div data-testid="brands-page" className="pt-16">
            <section className="max-w-7xl mx-auto px-6 md:px-12 pt-12 pb-10">
                <div className="overline mb-4">Brand Directory</div>
                <h1 className="font-serif text-5xl md:text-7xl text-inorbit-blue leading-[1.02] tracking-tight optical-shift">
                    {brands.length}+ brands. <span className="script-tag text-inorbit-orange script-overlap text-6xl md:text-8xl">across India.</span>
                </h1>
                <p className="mt-6 text-text-secondary text-lg max-w-2xl">
                    Live brand directory mirrored from Inorbit Malls (Malad, Vashi, Hyderabad, Vadodara). Shop direct, in-store or via Inorbit One delivery.
                </p>
            </section>

            <section className="border-y border-border-subtle bg-cream sticky top-20 z-20" data-testid="brands-filter-bar">
                <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex flex-wrap items-center gap-3">
                    <div className="flex items-center gap-2 bg-white border border-border-subtle px-4 py-2 flex-1 min-w-[220px] rounded">
                        <Search size={16} className="text-text-tertiary" />
                        <input
                            value={q} onChange={(e) => setQ(e.target.value)}
                            placeholder="Search brands…"
                            className="flex-1 outline-none text-sm bg-transparent"
                            data-testid="brands-search-input"
                        />
                    </div>
                    <div className="flex gap-1 flex-wrap" data-testid="alpha-filter">
                        <button onClick={() => setLetter('')} className={`text-[11px] uppercase tracking-widest font-bold w-7 h-7 transition-colors ${!letter ? 'bg-inorbit-blue text-inorbit-yellow' : 'text-inorbit-blue hover:bg-inorbit-blue hover:text-inorbit-yellow'}`}>All</button>
                        {LETTERS.map((l) => (
                            <button
                                key={l}
                                onClick={() => setLetter(l)}
                                className={`text-[11px] uppercase tracking-widest font-bold w-7 h-7 transition-colors ${letter === l ? 'bg-inorbit-blue text-inorbit-yellow' : 'text-inorbit-blue hover:bg-inorbit-blue hover:text-inorbit-yellow'}`}
                                data-testid={`alpha-${l}`}
                            >
                                {l}
                            </button>
                        ))}
                    </div>
                    <div className="text-xs uppercase tracking-widest text-text-tertiary ml-auto">{filtered.length} brands</div>
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-6 md:px-12 py-16">
                {grouped.length === 0 ? (
                    <div className="text-center text-text-tertiary py-20">No brands match. Run the catalog scrape from /admin/catalog.</div>
                ) : grouped.map(([k, items]) => (
                    <div key={k} className="mb-16">
                        <div className="flex items-baseline gap-4 mb-6 pb-3 border-b border-border-subtle">
                            <h2 className="font-serif text-5xl text-inorbit-blue">{k}</h2>
                            <span className="text-xs uppercase tracking-widest text-text-tertiary">{items.length} brands</span>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 stagger">
                            {items.map((b) => (
                                <Link
                                    to={`/shop?brand=${encodeURIComponent(b.name)}`}
                                    key={b.slug}
                                    className="block border border-border-subtle bg-white p-5 hover:border-inorbit-orange hover:shadow-lg transition-all group rounded-md"
                                    data-testid={`brand-card-${b.slug}`}
                                >
                                    <div className="aspect-[3/2] bg-cream rounded mb-4 overflow-hidden flex items-center justify-center p-3">
                                        <BrandLogo brand={b} className="max-w-full max-h-full object-contain" />
                                    </div>
                                    <div className="font-serif text-lg text-inorbit-blue group-hover:text-inorbit-orange transition-colors leading-tight truncate">{b.name}</div>
                                    <div className="text-[10px] tracking-widest uppercase text-text-tertiary mt-2">{b.product_count} {b.product_count === 1 ? 'product' : 'products'}</div>
                                </Link>
                            ))}
                        </div>
                    </div>
                ))}
            </section>
        </div>
    );
}
